import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { message } from "antd";
import { LoadingOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { useTheme } from "../../ThemeContext";

const AuthSuccess = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(true);
  const [processingStep, setProcessingStep] = useState("Verifying authentication...");
  const { themeMode, currentTheme } = useTheme();
  const hasProcessedAuth = useRef(false);

  /**
   * Extracts authentication data from cookies set by the OAuth callback.
   * Handles double-encoded JSON and escaped characters in cookie values.
   * @returns {Object|null} The parsed auth data or null if not found
   */
  const extractAuthDataFromCookies = () => {
    try {
      const cookies = document.cookie.split(";").reduce((acc, cookie) => {
        const [key, value] = cookie.trim().split("=");
        if (key && value) {
          acc[key] = decodeURIComponent(value);
        }
        return acc;
      }, {});

      if (!cookies || Object.keys(cookies).length === 0) {
        return null;
      }

      const possibleAuthCookies = [
        "access_token", "auth_token", "jwt_token", "token", "auth_data",
        "user_data", "user_info", "staff_info", "staff_data", "authentication"
      ];

      let authData = {};

      // Check for individual authentication components
      if (cookies.access_token) {
        authData.access_token = cookies.access_token;
      }
      if (cookies.auth_token) {
        authData.access_token = cookies.auth_token;
      }
      if (cookies.staff_info) {
        try {
          authData.staff = JSON.parse(cookies.staff_info);
        } catch (e) {
          // Silent fallback
        }
      }
      if (cookies.user_info) {
        try {
          const userInfo = JSON.parse(cookies.user_info);
          if (userInfo.email || userInfo.id) {
            authData.staff = userInfo;
          }
        } catch (e) {
          // Silent fallback
        }
      }
      if (cookies.expires_in) {
        authData.expires_in = parseInt(cookies.expires_in);
      }

      // Check for complete auth data in single cookies
      for (const cookieName of possibleAuthCookies) {
        if (cookies[cookieName]) {
          try {
            const parsedData = JSON.parse(cookies[cookieName]);
            if (parsedData && typeof parsedData === "object") {
              return parsedData;
            }
          } catch (e) {
            authData[cookieName] = cookies[cookieName];
          }
        }
      }

      return Object.keys(authData).length > 0 ? authData : null;
    } catch (error) {
      return null;
    }
  };

  /**
   * Alternative method to extract auth data from URL parameters or postMessage
   * This is more reliable for cross-origin scenarios
   */
  const extractAuthDataFromAlternativeSources = () => {
    // Check URL parameters first
    const urlParams = new URLSearchParams(window.location.search);
    const authDataFromUrl = {};
    
    const authUrlParams = ['token', 'access_token', 'auth_token', 'user_data', 'user_info', "api_key"];
    authUrlParams.forEach(param => {
      const value = urlParams.get(param);
      if (value) {
        authDataFromUrl[param] = value;
      }
    });
    
    if (Object.keys(authDataFromUrl).length > 0) {
      return authDataFromUrl;
    }
    
    // Set up postMessage listener for auth data
    return new Promise((resolve) => {
      const handleMessage = (event) => {
        if (event.data && typeof event.data === 'object' && event.data.type === 'oauth_success') {
          window.removeEventListener('message', handleMessage);
          resolve(event.data.authData);
        }
      };
      
      window.addEventListener('message', handleMessage);
      
      setTimeout(() => {
        window.removeEventListener('message', handleMessage);
        resolve(null);
      }, 2000);
      
      if (window.opener) {
        window.opener.postMessage({ type: 'request_auth_data' }, '*');
      }
    });
  };

  /**
   * Handles the OAuth success flow by extracting auth data from URL parameters,
   * storing it in sessionStorage, and redirecting to the home page.
   */
  const handleOAuthSuccessFlow = async () => {
    try {
      const success = searchParams.get("success");
      
      if (success !== "true") {
        navigate("/");
        return;
      }

      // Step 1: Extract authentication data from URL parameters (Primary Method)
      setProcessingStep("Extracting authentication data...");
      await new Promise((resolve) => setTimeout(resolve, 500));

      let authData = null;
      
      // Method 1: Base64-encoded auth_data parameter (PRIMARY)
      const authDataEncoded = searchParams.get('auth_data');
      if (authDataEncoded) {
        try {
          const authDataDecoded = atob(authDataEncoded);
          authData = JSON.parse(authDataDecoded);
        } catch (e) {
          // Silent fallback
        }
      }
      
      // Method 2: URL-encoded JSON auth_data parameter (FALLBACK)
      if (!authData) {
        const authDataParam = searchParams.get("auth_data");
        if (authDataParam) {
          try {
            authData = JSON.parse(decodeURIComponent(authDataParam));
          } catch (e) {
            // Silent fallback
          }
        }
      }
      
      // Method 3: Individual URL parameters (FALLBACK)
      if (!authData) {
        const urlAuthData = {};
        ['access_token', 'token', 'user_info', 'staff_info'].forEach(param => {
          const value = searchParams.get(param);
          if (value) {
            urlAuthData[param] = value;
          }
        });
        
        if (Object.keys(urlAuthData).length > 0) {
          authData = urlAuthData;
        }
      }
      
      // Method 4: Cookies (LAST RESORT)
      if (!authData) {
        authData = extractAuthDataFromCookies();
      }

      if (!authData) {
        setProcessingStep("Trying alternative authentication sources...");
        
        const alternativeAuthData = await extractAuthDataFromAlternativeSources();
        
        if (!alternativeAuthData) {
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const hashAuthData = {};
          
          ['access_token', 'token', 'user_info', 'auth_data'].forEach(param => {
            const value = hashParams.get(param);
            if (value) {
              hashAuthData[param] = value;
            }
          });
          
          if (Object.keys(hashAuthData).length > 0) {
            authData = hashAuthData;
          } else {
            throw new Error("No authentication data found");
          }
        } else {
          authData = alternativeAuthData;
        }
      }

      // Step 2: Parse user information
      setProcessingStep("Processing user information...");
      await new Promise((resolve) => setTimeout(resolve, 500));

      let staffInfo = null;
      
      if (authData.user_info) {
        if (typeof authData.user_info === 'object') {
          staffInfo = authData.user_info;
        } else {
          try {
            let userInfoStr = authData.user_info;
            
            if (userInfoStr.startsWith('"') && userInfoStr.endsWith('"')) {
              userInfoStr = userInfoStr.slice(1, -1);
            }
            
            let cleanedUserInfo = userInfoStr
              .replace(/\\054/g, ',')
              .replace(/\\\\/g, '\\')
              .replace(/\\"/g, '"');
            
            staffInfo = JSON.parse(cleanedUserInfo);
          } catch (e) {
            staffInfo = authData.user_info;
          }
        }
      } else if (authData.staff) {
        staffInfo = authData.staff;
      }

      // Step 3: Store authentication data in sessionStorage
      setProcessingStep("Setting up your session...");
      await new Promise((resolve) => setTimeout(resolve, 500));

      if (authData.access_token) {
        sessionStorage.setItem("access_token", authData.access_token);
        sessionStorage.setItem("api_key", authData.api_key);
      } else {
        throw new Error("No access_token found in auth data");
      }

      if (staffInfo) {
        const staffInfoStr = JSON.stringify(staffInfo);
        sessionStorage.setItem("staff_info", staffInfoStr);

        if (staffInfo.customerId) {
          sessionStorage.setItem("customerId", staffInfo.customerId);
        }
      } else {
        throw new Error("No staff information found in auth data");
      }

      const expiresIn = authData.expires_in || 1800;
      const expiresAt = Date.now() + (expiresIn * 1000);
      sessionStorage.setItem("token_expires_at", expiresAt.toString());

      // Step 4: Complete authentication
      setProcessingStep("Finalizing authentication...");
      setIsAuthenticated(true);
      await new Promise((resolve) => setTimeout(resolve, 500));

      setProcessingStep("Authentication successful!");
      setIsProcessing(false);
      message.success("Login successful! Redirecting to dashboard...");
      
      await new Promise((resolve) => setTimeout(resolve, 100));

      setTimeout(() => {
        navigate("/home");
        window.location.reload();
      }, 1000);

    } catch (error) {
      setProcessingStep("Authentication failed");
      setIsProcessing(false);
      message.error("Failed to complete authentication. Redirecting to login...");
      
      // Clear any partial session data
      sessionStorage.removeItem("access_token");
      sessionStorage.removeItem("staff_info");
      sessionStorage.removeItem("customerId");
      sessionStorage.removeItem("token_expires_at");
      
      setTimeout(() => {
        navigate("/");
      }, 2000);
    }
  };

  // Trigger OAuth success flow when component mounts
  useEffect(() => {
    // Prevent running the authentication flow multiple times
    if (!hasProcessedAuth.current) {
      hasProcessedAuth.current = true;
      handleOAuthSuccessFlow();
    }
  }, [searchParams, navigate]);

  // Popup styling similar to BillingRestrictionPopup
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10000,
    animation: 'fadeIn 0.3s ease-out'
  };

  const popupStyle = {
    backgroundColor: themeMode === 'dark' ? '#04002b' : '#ffffff',
    color: themeMode === 'dark' ? '#ffffff' : '#000000',
    borderRadius: '16px',
    padding: '0',
    maxWidth: '500px',
    width: '90%',
    maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: themeMode === 'dark' 
      ? '0 20px 40px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.1)'
      : '0 20px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
    border: themeMode === 'dark' ? '1px solid #302d4c' : '1px solid #e9e8ea',
    animation: 'slideIn 0.3s ease-out',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif"
  };

  const headerStyle = {
    padding: '24px 24px 0 24px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderBottom: `1px solid ${themeMode === 'dark' ? '#302d4c' : '#e9e8ea'}`,
    paddingBottom: '16px',
    marginBottom: '24px'
  };

  const titleStyle = {
    fontSize: '20px',
    fontWeight: '600',
    color: themeMode === 'dark' ? '#ffffff' : '#000000',
    margin: '0',
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  };

  const contentStyle = {
    padding: '0 24px',
    marginBottom: '32px',
    textAlign: 'center'
  };

  const iconStyle = {
    fontSize: '3rem',
    color: isProcessing ? '#1890ff' : '#52c41a',
    animation: isProcessing ? 'spin 1s linear infinite' : 'none',
    marginBottom: '1.5rem'
  };

  const messageStyle = {
    fontSize: '16px',
    lineHeight: '1.6',
    color: themeMode === 'dark' ? '#ffffff' : '#000000',
    margin: '0',
    textAlign: 'center'
  };

  const successIndicatorStyle = {
    marginTop: '1rem',
    padding: '0.5rem 1rem',
    backgroundColor: themeMode === 'dark' ? '#1a3a1a' : '#f6ffed',
    border: `1px solid ${themeMode === 'dark' ? '#4a8c4a' : '#b7eb8f'}`,
    borderRadius: '6px',
    color: themeMode === 'dark' ? '#6dc46d' : '#389e0d',
    fontSize: '0.9rem'
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideIn {
            from { 
              opacity: 0; 
              transform: translateY(-20px) scale(0.95); 
            }
            to { 
              opacity: 1; 
              transform: translateY(0) scale(1); 
            }
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={overlayStyle}>
        <div style={popupStyle}>
          <div style={headerStyle}>
            <h2 style={titleStyle}>
              {isProcessing ? "Processing Authentication" : "Authentication Complete"}
            </h2>
          </div>
          
          <div style={contentStyle}>
            <div style={iconStyle}>
              {isProcessing ? (
                <LoadingOutlined />
              ) : (
                <CheckCircleOutlined />
              )}
            </div>
            
            <p style={messageStyle}>{processingStep}</p>
            
            {!isProcessing && (
              <div style={successIndicatorStyle}>
                Redirecting to dashboard...
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AuthSuccess;
